import type { PropertyStatus, AuctionInfo } from '@unreserved/types';
import { calculateAuctionProgress } from '@unreserved/utils/property-card';
import type { IconComponent } from '@unreserved/types';

// Constants
export const STATUS_BASE_CLASSES =
  'inline-flex items-center h-6 rounded-lg px-2 py-[6px]';

export const AUCTION_STATUS = {
  STARTED: 'Auction started',
  PAUSED: 'Auction Paused',
  STARTING: 'Auction Starts in',
} as const;

export const EXTERNAL_LINKS = {
  REALESTATE: {
    url: 'https://www.realestate.com.au/agency/unreserved-lite-WQVTXM',
    label: 'realestate.com.au',
  },
  DOMAIN: {
    url: 'https://www.domain.com.au/real-estate-agencies/unreserved-lite-40001',
    label: 'domain.com.au',
  },
} as const;

export const OFFER_AMOUNT_LABEL = 'Offer Amount';

export const NO_PROPERTIES_MESSAGE = " You don't have any properties yet.";

export const PRIVATE_SALE_LIST_CARD_COPY = {
  CONTACT_VENDOR: 'Contact Vendor for sale price',
  CONTACT_UNRESERVED: 'Contact Unreserved for sale price',
} as const;

// Icon configuration - apps should provide their own icons
// This is a placeholder structure that apps can override
export interface DashboardIcons {
  LIVE_AUCTION?: IconComponent;
  AREA?: IconComponent;
  BEDROOM?: IconComponent;
}

// Utility Functions

// Map database listing_type to frontend SaleType
export function mapListingTypeToSaleType(
  listingType: 'auction' | 'private_sale' | 'reverse_auction',
): 'Live Auction' | 'Private Sale' | 'Reverse Auction' {
  switch (listingType) {
    case 'auction':
      return 'Live Auction';
    case 'private_sale':
      return 'Private Sale';
    case 'reverse_auction':
      return 'Reverse Auction';
    default:
      return 'Private Sale';
  }
}

/**
 * Map database listing_status to frontend PropertyStatus
 * For Live Auction and Reverse Auction: uses both listing_status and auction_status
 * For Private Sale: uses only listing_status (auction_status is ignored)
 */
export function mapListingStatusToPropertyStatus(
  listingType: 'auction' | 'private_sale' | 'reverse_auction',
  listingStatus:
    | 'draft'
    | 'active'
    | 'under_offer'
    | 'off_the_market'
    | 'passed_in'
    | 'disabled'
    | 'deleted'
    | 'sold',
  auctionStatus?:
    | 'to_be_started'
    | 'in_progress'
    | 'paused'
    | 'completed'
    | 'cancelled',
): PropertyStatus {
  // Live Auction and Reverse Auction: use auction_status to show granular status
  if (listingType === 'auction' || listingType === 'reverse_auction') {
    if (listingStatus === 'sold') {
      return 'Sold';
    }
    switch (auctionStatus) {
      case 'to_be_started':
        return 'Yet to start';
      case 'in_progress':
        return 'In Progress';
      case 'paused':
        return 'Paused';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
    }
  }

  // Private Sale (and auction/reverse_auction with no auctionStatus): use listing_status
  switch (listingStatus) {
    case 'active':
      return 'In Progress';
    case 'sold':
      return 'Sold';
    case 'under_offer':
      return 'Under Offer';
    case 'off_the_market':
      return 'Off the market';
    case 'disabled':
      return 'Off the market';
    case 'deleted':
      return 'Off the market';
    case 'passed_in':
      return 'Under Offer';
    case 'draft':
      return 'Draft';
    default:
      return 'Inactive';
  }
}

/**
 * Map database auction_status to frontend AuctionInfo
 * Only used for Live Auction and Reverse Auction (not Private Sale)
 */
export function mapAuctionStatusToAuctionInfo(
  listingType: 'auction' | 'private_sale' | 'reverse_auction',
  auctionStatus:
    | 'to_be_started'
    | 'in_progress'
    | 'paused'
    | 'completed'
    | 'cancelled',
  auctionStartDate?: string | null,
  auctionEndDate?: string | null,
  timeLeft?: string,
): AuctionInfo | undefined {
  // Private Sale doesn't use auction status (auction_status is ignored)
  if (listingType === 'private_sale') {
    return undefined;
  }

  // For auction and reverse_auction, require dates
  if (!auctionStartDate && !auctionEndDate) {
    return undefined;
  }

  switch (auctionStatus) {
    case 'in_progress':
      return {
        status: 'Auction started',
        timeLeft,
        progress: calculateAuctionProgress(auctionStartDate, auctionEndDate),
        auctionStartDate: auctionStartDate ?? undefined,
      };
    case 'paused':
      return {
        status: 'Auction Paused',
        timeLeft,
        auctionStartDate: auctionStartDate ?? undefined,
      };
    case 'to_be_started':
      return {
        status: 'Auction Starts in',
        timeLeft,
        auctionStartDate: auctionStartDate ?? undefined,
      };
    case 'completed':
    case 'cancelled':
      return undefined;
    default:
      return undefined;
  }
}
